import { useMemo } from 'react';

import { useAppSelector } from '@store/store.tsx';
import { logInfo } from '@tools/LogsHandler.tsx';

export const APPOINTMENT_SCOPES = {
  APPOINTMENTS_READ: 'appointments:read',
  APPOINTMENTS_WRITE: 'appointments:write',
  APPOINTMENTS_HISTORY_READ: 'appointments:history:read',
  APPOINTMENTS_HISTORY_WRITE: 'appointments:history:write',
  APPOINTMENTS_HISTORY_WRITE_OWN: 'appointments:history:write_own',
  APPOINTMENTS_HISTORY_DELETE: 'appointments:history:delete',
  ADD_CHARGE: 'sale_payments:add',
} as const;

type ScopeValue = (typeof APPOINTMENT_SCOPES)[keyof typeof APPOINTMENT_SCOPES];

export interface AppointmentPermissions {
  isSuperuser: boolean;
  scopes: Set<string>;
  canViewAppointments: boolean;
  canManageAppointments: boolean;
  canViewHistory: boolean;
  canManageHistory: boolean;
  canManageHistoryOwn: boolean;
  canDeleteHistory: boolean;
  canAddCharge: boolean;
  restrictToOwnAppointments: boolean;
  restrictToOwnHistory: boolean;
}

const PROFESSIONAL_SCOPE_TOKENS = new Set(['services', 'servicios', 'support', 'erp:staff:services']);
const GROUP_SCOPE_MAP: Record<string, readonly ScopeValue[]> = {
  reception: [
    APPOINTMENT_SCOPES.APPOINTMENTS_READ,
    APPOINTMENT_SCOPES.APPOINTMENTS_WRITE,
    APPOINTMENT_SCOPES.APPOINTMENTS_HISTORY_READ,
    APPOINTMENT_SCOPES.APPOINTMENTS_HISTORY_WRITE,
  ],
  recepcion: [
    APPOINTMENT_SCOPES.APPOINTMENTS_READ,
    APPOINTMENT_SCOPES.APPOINTMENTS_WRITE,
    APPOINTMENT_SCOPES.APPOINTMENTS_HISTORY_READ,
    APPOINTMENT_SCOPES.APPOINTMENTS_HISTORY_WRITE,
  ],
  services: [
    APPOINTMENT_SCOPES.APPOINTMENTS_READ,
    APPOINTMENT_SCOPES.APPOINTMENTS_HISTORY_READ,
    APPOINTMENT_SCOPES.APPOINTMENTS_HISTORY_WRITE,
  ],
  servicios: [
    APPOINTMENT_SCOPES.APPOINTMENTS_READ,
    APPOINTMENT_SCOPES.APPOINTMENTS_HISTORY_READ,
    APPOINTMENT_SCOPES.APPOINTMENTS_HISTORY_WRITE,
  ],
};

const normalizeString = (value: string | null | undefined): string | null => {
  if (!value) {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed.toLowerCase() : null;
};

export const computeAppointmentPermissions = (
  groups: string[] | undefined,
  roles: string[] | undefined,
  permissions: string[] | undefined,
): AppointmentPermissions => {
  const normalizedRoles = new Set(
    (roles ?? [])
      .map(normalizeString)
      .filter((entry): entry is string => Boolean(entry)),
  );

  const isSuperuser = normalizedRoles.has('superuser');

  const normalizedGroups = (groups ?? [])
    .map(normalizeString)
    .filter((entry): entry is string => Boolean(entry));

  const normalizedGroupSet = new Set(normalizedGroups);

  const scopes = new Set<string>(permissions);

  normalizedGroupSet.forEach((group) => {
    GROUP_SCOPE_MAP[group]?.forEach((scope) => scopes.add(scope));
  });

  if (isSuperuser) {
    Object.values(APPOINTMENT_SCOPES).forEach((scope) => scopes.add(scope));
  }

  const canViewAppointments = scopes.has(APPOINTMENT_SCOPES.APPOINTMENTS_READ);
  const canManageAppointments = scopes.has(APPOINTMENT_SCOPES.APPOINTMENTS_WRITE);
  const canViewHistory = scopes.has(APPOINTMENT_SCOPES.APPOINTMENTS_HISTORY_READ)
                                || scopes.has(APPOINTMENT_SCOPES.APPOINTMENTS_HISTORY_WRITE)
                                || scopes.has(APPOINTMENT_SCOPES.APPOINTMENTS_HISTORY_WRITE_OWN)
                                || scopes.has(APPOINTMENT_SCOPES.APPOINTMENTS_HISTORY_DELETE);
  const canManageHistory = scopes.has(APPOINTMENT_SCOPES.APPOINTMENTS_HISTORY_WRITE);
  const canManageHistoryOwn = scopes.has(APPOINTMENT_SCOPES.APPOINTMENTS_HISTORY_WRITE_OWN);
  const canDeleteHistory = scopes.has(APPOINTMENT_SCOPES.APPOINTMENTS_HISTORY_DELETE);
  const canAddCharge = scopes.has(APPOINTMENT_SCOPES.ADD_CHARGE);

  const professionalTokenMatch = Array.from(
    new Set<string>([
      ...normalizedGroupSet,
      ...normalizedRoles,
    ]),
  ).some((token) => PROFESSIONAL_SCOPE_TOKENS.has(token));

  const restrictToOwn =
    !isSuperuser && professionalTokenMatch && !scopes.has(APPOINTMENT_SCOPES.APPOINTMENTS_WRITE);

  logInfo('     ** computeAppointmentPermissions **: ', {
    isSuperuser,
    scopes,
    canViewAppointments,
    canManageAppointments,
    canViewHistory,
    canManageHistory,
    canManageHistoryOwn,
    canDeleteHistory,
    canAddCharge,
    restrictToOwnAppointments: restrictToOwn,
    restrictToOwnHistory: restrictToOwn,
  });

  return {
    isSuperuser,
    scopes,
    canViewAppointments: isSuperuser || canViewAppointments,
    canManageAppointments: isSuperuser || canManageAppointments,
    canViewHistory: isSuperuser || canViewHistory,
    canManageHistory: isSuperuser || canManageHistory,
    canManageHistoryOwn: isSuperuser || canManageHistoryOwn,
    canDeleteHistory: isSuperuser || canDeleteHistory,
    canAddCharge: isSuperuser || canAddCharge,
    restrictToOwnAppointments: restrictToOwn,
    restrictToOwnHistory: restrictToOwn,
  };
};

export const useAppointmentPermissions = (): AppointmentPermissions => {
  const profile = useAppSelector((state) => state.user.profile);
  const groups = profile?.groups ?? [];
  const roles = profile?.role ?? [];
  const permissions = (profile?.permissions ?? []) as string[];
  logInfo('     ** useAppointmentPermissions **: ', {permissions, groups, roles})

  const groupKey = [...groups].sort().join('|');
  const roleKey = [...roles].sort().join('|');
  const permissionsKey = [...permissions].sort().join('|');

  return useMemo(
    () => computeAppointmentPermissions(groups, roles, permissions),
    [groupKey, roleKey, permissionsKey],
  );
};

export type { ScopeValue as AppointmentScope };
